export interface ViewerOutgoingApi {
    close: RequestModelClose;
    discard: RequestModelDiscard;
    examinationStatus: RequestModelExaminationStatus;
    viewImages: RequestModelViewStudies;
    showImage: ModelShowImage;
    deleteKeyImage: ModelKeyImage;
    usStressEchoAssociateImages: ModelUSStressEchoAssociateImages;
    usStressEchoSelectStage: string;
    usStressEchoSelectView: string;
    // Rimosso perché non comune tra Review e Zefiro - usMeasureShow: ModelUSMeasureShow;
}

export interface RequestModelClose {
    force: boolean;
}

export interface RequestModelDiscard {
    ask: boolean;
}

export interface RequestModelExaminationStatus {
    idExaminations: string[];
    examinationStatus: ExaminationStatusForImaging;
}

export type ExaminationStatusForImaging =
    | 'Unknown' // Error status
    | 'Open' // Normal examination state
    | 'Close' // Approve/Sign examination
    | 'Closed' // Opened an approved/signed examination
    | 'ReOpened' // Undo sign/approve
    | 'Lock'; // Unused - cannot open locked examination at the moment

export interface RequestModelViewStudies {
    studies: ModelQueryDicomStudy[];
    viewForConsultation: boolean;
    discardOption: ModelDiscard;
    iSSource: ModelISSource;
    screeningMGOption: ModelScreeningMG;
}

export interface ModelQueryDicomStudy {
    tags: Record<string, string>;
}

export interface ModelDiscard {
    ask: boolean;
    action: 'Discard' | 'Append';
}

export type ModelISSource = 'Unknown' | 'Radio' | 'Cardio';

export interface ModelScreeningMG {
    enable: boolean;
    role: ScreeningUserRole;
    userID: string;
    roleLabel: number;
}

export enum ScreeningUserRole {
    None = 0,
    Reader = 1,
    Reviewer = 2,
    OnlyView = 3,
}

export interface ModelKeyImage {
    instanceUID: string;
    isDeletable: boolean;
    repositoryKey: string;
    requestDeleteAll: boolean;
    requestRefreshIcon: boolean;
    nPla: PacsTuple | null;
}

export interface PacsTuple {
    patientBirthDate: string;
    patientID: string;
    patientName: string;
    patientSex: string;
    studyAccessionNumber: string;
    studyDate: string;
    studyID: string;
    studyInstanceUID: string;
}

export interface ModelUSStressEchoAssociateImages {
    stages: string[];
    views: string[];
}

export interface ModelShowImage {
    structuredReportSOPInstanceUID?: string;
    referenceSOPInstanceUID: string;
    frame?: number;
    graphicCoord?: string;
    presentationStateSOPInstaceUID?: string;
}

export interface ModelUSMeasureShowXML {
    measureGuid: string;
    measureIsCalculated: boolean;
    measureLabel: string;
}

export interface ModelUSMeasureShowJSON {
    referenceSOPInstanceUID?: string;
    frame?: number;
    measureGuid: string;
    instanceID: string;
}

export interface ModelUSMeasureShow {
    xml: ModelUSMeasureShowXML;
    json: ModelUSMeasureShowJSON;
}

export type OpenImagesOptions = Omit<RequestModelViewStudies, 'studies'>;

export const DEFAULT_OPEN_IMAGES_OPTIONS: OpenImagesOptions = {
    discardOption: {
        ask: false,
        action: 'Discard',
    },
    iSSource: 'Cardio',
    screeningMGOption: {
        enable: false,
        role: ScreeningUserRole.None,
        roleLabel: 0,
        userID: '',
    },
    viewForConsultation: false,
};

export interface ViewerIncomingApi {
    studyInstanceUID: unknown;
    addKeyImage: ModelKeyImage;
    deleteKeyImage: ModelKeyImage;
    refreshKeyImage: ModelKeyImage;
    keyImagesInfo: ModelKeyImageInstanceInfo[];
    usMeasureDeleteSourceAndImportToReport: (measure: ModelUSMeasure, channel: string, id: string) => unknown;
    usMeasureGetReportInfo: (nPla: PacsTuple, channel: string, id: string) => unknown;
    usMeasureImportToReport: ModelUSMeasure;
    usMeasurePmiImportToReport: ModelUSMeasure;
    addStructuredReport: ModelStructuredReport;
    deleteStructuredReport: ModelStructuredReport;
    addKeyObjectSelection: unknown;
    deleteKeyObjectSelection: unknown;
    xmlCaasData: ModelXmlCaasData;
    xmlCaasMrData: ModelXmlCaasData;
    xmlTeraReconData: string;
    usStressEchoInit: ModelUSStressEchoInit;
    usStressEchoSelect: ModelUSStressEchoSelect;
    lvStrainStart: unknown;
    lvStrainEnd: unknown;
    lvStrainResultClear: unknown;
    lvStrainResult: string;
    rvStrainStart: unknown;
    rvStrainEnd: unknown;
    rvStrainResultClear: unknown;
    rvStrainResult: string;
    lvWallMotionStart: unknown;
    lvWallMotionEnd: unknown;
    lvWallMotionResultClear: unknown;
    lvWallMotionResult: string;
    addGroupMeasure: unknown;
    deleteMeasure: unknown;
    modifyMeasure: unknown;
}

export interface ModelKeyImageInstanceInfo {
    studyUID: string;
    seriesUID: string;
    instanceUID: string;
    isKeyImage: boolean;
    isDeletable: boolean;
    isReadOnly: boolean;
    repositoryKey: string;
    nPla: PacsTuple;
}

export interface ModelUSMeasure {
    nPla: PacsTuple;
    repositoryId: string;
}

export interface ModelUSMeasureReportInfo {
    id: string;
    bsa?: number;
    isMeanEnable: boolean;
    applicationKind: ApplicationKind;
}

export enum ApplicationKind {
    Cardiac = 2,
    Vascular = 3,
    CardiacPediatric = 8,
}

export interface ModelStructuredReport {
    nPla: PacsTuple;
    repositoryKey: string;
    instanceUID: string;
}

export interface ModelUSStressEchoInit {
    nPla: string;
    stages: string[];
    views: string[];
    studyUID: string;
    heartRate: ModelUSStressEchoHeartRate;
}

export interface ModelUSStressEchoHeartRate {
    items: ModelUSStressEchoHeartRateItem[];
}

export interface ModelUSStressEchoHeartRateItem {
    stage: string;
    view: string;
    heartRate: number;
    acquisition: string;
}

export interface ModelUSStressEchoSelect {
    nPla: string;
    stages: string[];
    views: string[];
    studyUID: string;
    rows: number;
    columns: number;
    content: ModelUSStressEchoItem[];
}

export interface ModelUSStressEchoItem {
    stage: string;
    view: string;
}

export interface ModelXmlCaasData {
    nPla: PacsTuple;
    xmlMessage: string;
}
